import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    Box,
    Paper,
} from '@mui/material';
import {
    Assessment as EvaluationIcon,
    FeedbackOutlined as FeedbackIcon,
} from '@mui/icons-material';
import { TextFields as InputIcon } from '@mui/icons-material';

export default function FormTypeSelector() {
    const navigate = useNavigate();

    const formTypes = [
        {
            id: 'evaluation',
            title: 'Evaluation Form',
            description: 'Create forms for assessments and evaluations with scoring.',
            icon: <EvaluationIcon sx={{ fontSize: 48 }} />,
            color: '#667eea',
            features: [
                'Questions with scoring',
                'Auto-reject options',
                'Multiple question types',
                'Hidden scoring logic',
            ],
        },
        {
            id: 'input-detail',
            title: 'Input Detail Form',
            description: 'Google Forms-style data collection forms without scoring.',
            icon: <InputIcon sx={{ fontSize: 48 }} />,
            color: '#f093fb',
            features: [
                'Multiple field types',
                'Placeholder text',
                'Optional/Required fields',
                'Clean data collection',
            ],
        },
        {
            id: 'feedback',
            title: 'Feedback Form',
            description: 'Collect ratings, feedback, and testimonials.',
            icon: <FeedbackIcon sx={{ fontSize: 48 }} />,
            color: '#4facfe',
            features: [
                'Rating scales',
                'Text feedback',
                'Optional questions',
                'Simple feedback collection',
            ],
        },
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            {/* Header */}
            <Box sx={{ mb: 6, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
                    Create a New Form
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Choose the type of form you want to create
                </Typography>
            </Box>

            {/* Form Type Cards */}
            <Grid container spacing={3}>
                {formTypes.map((type) => (
                    <Grid item xs={12} sm={6} md={4} key={type.id}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                borderTop: `4px solid ${type.color}`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: 4,
                                    transform: 'translateY(-8px)',
                                },
                            }}
                        >
                            <CardContent sx={{ flex: 1 }}>
                                <Box sx={{ color: type.color, mb: 2 }}>
                                    {type.icon}
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {type.title}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                    {type.description}
                                </Typography>

                                <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Features:
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                    {type.features.map((feature, idx) => (
                                        <Typography
                                            component="li"
                                            variant="caption"
                                            key={idx}
                                            sx={{ display: 'list-item', mb: 0.5 }}
                                        >
                                            {feature}
                                        </Typography>
                                    ))}
                                </Box>
                            </CardContent>
                            <CardActions>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => navigate(`/form-builder/${type.id}/new`)}
                                    sx={{ bgcolor: type.color }}
                                >
                                    Create
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}
